import { AxiosInstance } from 'axios';
import { z } from 'zod';

import { RegistryItemStatus } from '../types/registry';
import { QuickSearchSuggestionsResponseSchema } from '../types/suggestion';
import { SuggestionApi } from '../types/suggestionApi';
import { createRequestConfig, globalSearchApi } from './';
import { qObjectResourceMap } from '../utils/eventFormUtils';

// Every resource type an event template field can link to - derived from qObjectResourceMap
// so it can't drift out of sync as field types are added.
const RELATED_RECORD_RESOURCES = [...new Set(Object.values(qObjectResourceMap).flat())].join(',');

export const RelatedRecordSearchResultSchema = z.object({
  resource: z.string(),
  resourceSubType: z.string().nullish(),
  resourceSubTypeName: z.string().nullish(),
  name: z.string().nullish(),
  status: z.string().nullish(),
  identifiers: z.object({
    id: z.coerce.string(),
    code: z.string().nullish(),
    qri: z.string(),
  }),
});

export type RelatedRecordSearchResult = z.infer<typeof RelatedRecordSearchResultSchema>;

// Registry items are indexed per version and every version shares the item's QRI
const dedupeRegistryItems = (results: RelatedRecordSearchResult[]): RelatedRecordSearchResult[] => {
  const resultsByQri = new Map<string, RelatedRecordSearchResult>();

  results.forEach((result) => {
    const existing = resultsByQri.get(result.identifiers.qri);
    if (!existing || result.status === RegistryItemStatus.Effective) {
      resultsByQri.set(result.identifiers.qri, result);
    }
  });

  return [...resultsByQri.values()];
};

export const searchRelatedRecords = async (companyId: number, term: string): Promise<RelatedRecordSearchResult[]> => {
  const config = createRequestConfig({});
  const { data } = await globalSearchApi.get(
    `/v2/quicksearch?companyId=${companyId}&resources=${RELATED_RECORD_RESOURCES}&term=${term}&size=10`,
    config,
  );
  const results = z.array(RelatedRecordSearchResultSchema).parse(data.results);

  return dedupeRegistryItems(results);
};

const quickSearchApis: Record<string, SuggestionApi> = {};

class QuickSearchApi implements SuggestionApi {
  private domain: string;
  private searchAdapter: AxiosInstance;

  constructor(domain: string, searchAdapter: AxiosInstance) {
    this.domain = domain;
    this.searchAdapter = searchAdapter;
  }
  async suggest(company: number, search: string) {
    const config = createRequestConfig({});
    const results = await this.searchAdapter.get(
      `/v2/quicksearch?companyId=${company}&term=${search}&resources=${this.domain}`,
      config,
    );
    return QuickSearchSuggestionsResponseSchema.parse(results.data.results);
  }
}

export const quickSearchApiFactory = (domain: 'document' | 'quality-event' | 'supplier') => {
  if (quickSearchApis[domain] === undefined) {
    quickSearchApis[domain] = new QuickSearchApi(domain, globalSearchApi);
  }
  return quickSearchApis[domain]!;
};
